/* eslint-disable no-use-before-define, react/jsx-no-bind, no-underscore-dangle */
import { useEffect, useState, useRef, useContext } from 'react';
import './questions.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { GetAlgorithmAPI } from '../../API/api';
import Nav from '../../components/Nav/Nav';
import Loading from '../Loading/Loading';
import Wdim from '../Wdim/Wdim';
import QuestionCard from '../QuestionCard/QuestionCard';
import Reassurance from '../Reassurance/Reassurance';
import PageNames from '../../utils/PageNames';
import PageTypes from '../../utils/PageTypes';
import MetricsContext from '../../context/MetricsContext';
import ButtonNames from '../../utils/ButtonNames';
import { sanitizeBrowserStorageValue } from '../../utils';
import { getPreviousQuestionTransition } from './questionNavigation';

export default function Questions() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [number, setNumber] = useState(0);
  const [questions, setQuestions] = useState([]);
  const [questionsAmount, setQuestionsAmount] = useState(0);
  const [showReassurance, setShowReassurance] = useState(false);
  const [showWdim, setShowWdim] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressAmount, setProgressAmount] = useState(0);
  const [startDate, setStartDate] = useState(new Date());
  const [answerLogString, setAnswerLogString] = useState('');
  const mutationRef = useRef(answerLogString);
  const { language } = i18n;
  const { t } = useTranslation();
  const ageGroup = location.state?.ageGroup;
  const symptom = location.state?.symptom;
  const { updateUserMetric } = useContext(MetricsContext);
  const VERSION_STORAGE_KEY = '@kitoApp/version';
  const [, setActiveVersion] = useState(() => window.localStorage.getItem(VERSION_STORAGE_KEY) || 'Adult');

  const getAlgorithm = async () => {
    const data = await GetAlgorithmAPI(ageGroup, symptom, language);
    const response = await data.json();
    if (!data.ok) {
      navigate('/error', {
        state: { code: data.status, message: JSON.stringify(response) },
      });
      return null;
    }
    setAnswerLogString(response?.answerLog);
    return response?.questionList;
  };

  useEffect(() => {}, [showReassurance]);

  useEffect(() => {
    async function fetchQuestions() {
      if (location.state === undefined) {
        navigate('/assessment');
      } else {
        const newQuestions = await getAlgorithm();
        const storageSafeQuestions = sanitizeBrowserStorageValue(newQuestions);
        localStorage.setItem('questionsList', JSON.stringify(storageSafeQuestions));
        setQuestions(newQuestions);
        const qlength = newQuestions?.length;
        const progressAmt = (100 / qlength) * 0.01;
        setProgressAmount(progressAmt);
        setQuestionsAmount(qlength);
        setLoading(false);
        updateProgressBar(progressAmt);

        updateUserMetric(
          {
            name: PageNames.QUESTIONS_PAGE,
            pageType: PageTypes.QUESTION_METRIC,
            questionCode: newQuestions[number].code,
          },
          {
            algorithmData: {
              questionsList: newQuestions,
            },
          }
        );
      }
    }
    fetchQuestions();
  }, []);

  function showReassurancePage() {
    setShowReassurance((current) => !current);
    const dateFinal = new Date();
    updateUserMetric({
      name: PageNames.QUESTION_PAGE_REASSURANCE,
      pageType: PageTypes.DEFAULT,
      duration: Math.abs(dateFinal - startDate),
      interactions: [
        {
          name: ButtonNames.CONTINUE,
          time: new Date(),
        },
      ],
    });
  }

  function showWdimPage() {
    setShowWdim(!showWdim);
    const dateFinal = new Date();
    updateUserMetric({
      name: PageNames.QUESTIONS_PAGE,
      pageType: PageTypes.QUESTION_METRIC,
      questionCode: questions[number].code,
      duration: Math.abs(dateFinal - startDate),
      interactions: [
        {
          name: ButtonNames.WDYM,
          time: new Date(),
        },
      ],
    });
  }

  const updateProgressBar = (value) => {
    setProgress(value);
  };

  useEffect(() => {
    const algorithmLog = answerLogString.split('.');
    const questions = algorithmLog[2];
    if (questions && questions.length === questionsAmount) {
      navigate('/output', {
        state: {
          language,
          answers: `${answerLogString}.${uuid()}`,
          symptom: location.state?.symptom,
        },
      });
    }
  }, [answerLogString]);

  useEffect(() => {
    mutationRef.current = answerLogString;
  }, [answerLogString]);

  const getRedAnswerOutput = async (value) => {
    const ansWeight = questions[number].answerList[value - 1].weight;
    if (ansWeight === 3) {
      setAnswerLogString((prev) => {
        const updated = prev + value;
        return updated;
      });
      navigate('/output', {
        state: {
          language,
          answers: `${mutationRef.current + value}.${uuid()}`,
          redAnswer: true,
          symptom: location.state?.symptom,
        },
      });
    }
  };

  const nextQuestion = (e, answerText) => {
    const { value } = e.target;
    getRedAnswerOutput(value);
    const dateFinal = new Date();
    updateUserMetric({
      name: PageNames.QUESTIONS_PAGE,
      pageType: PageTypes.QUESTION_METRIC,
      questionCode: questions[number].code,
      duration: Math.abs(dateFinal - startDate),
      interactions: [
        {
          name: answerText,
          time: dateFinal,
        },
      ],
    });

    let nextQ = number + 1;

    const answerCond = questions[number].answerList[value - 1].conditional;
    if (answerCond !== '') {
      nextQ = questions.findIndex((question) => question._id === answerCond);
    }

    // Failsafe: Does not permit the next question to go back in the question list
    if (nextQ <= number) nextQ = number + 1;

    // For each conditional question jumped, add a '0' after the value at answerLogString
    let condAmount = '';
    const conditionalPassed = nextQ - number;
    if (conditionalPassed > 1) {
      for (let index = 0; index < conditionalPassed - 1; index += 1) {
        condAmount += '0';
      }
    }

    // Set the answers and go for the next question (or output if none)
    if (
      nextQ === Math.floor(questionsAmount / 2) ||
      (number < Math.floor(questionsAmount / 2) && nextQ > Math.floor(questionsAmount / 2))
    ) {
      setNumber(nextQ);
      setStartDate(new Date());
      if (condAmount) updateProgressBar(progress + condAmount.length * progressAmount);
      else updateProgressBar(progress + progressAmount);
      setAnswerLogString(answerLogString + value + condAmount);
      showReassurancePage();
    } else if (nextQ === questionsAmount) {
      setAnswerLogString(answerLogString + value + condAmount);
      setLoading(true);
    } else {
      setNumber(nextQ);
      setStartDate(new Date());
      const progressIncrement = condAmount ? condAmount.length * progressAmount : progressAmount;
      updateProgressBar(progress + progressIncrement);
      setAnswerLogString(answerLogString + value + condAmount);
    }

    // To remove the selected option.
    const options = document.getElementsByName('option');
    Array.from(options).forEach((optionInput) => {
      const selectedOption = optionInput;
      selectedOption.checked = false;
      const option = selectedOption.parentElement.parentElement;
      option.classList.remove('col_checkbox__component_checked', 'checked');
    });
  };

  const previousQuestion = () => {
    const transition = getPreviousQuestionTransition({
      number,
      questionsAmount,
      answerLogString,
      progress,
      progressAmount,
      showReassurance,
    });

    if (transition.type === 'navigateBack') {
      navigate(-1);
      return;
    }

    if (transition.toggleReassuranceBeforeUpdate) showReassurancePage();

    setNumber(transition.nextQuestion);
    updateProgressBar(transition.nextProgress);
    setAnswerLogString(transition.nextAnswerLogString);

    if (transition.toggleReassuranceAfterUpdate) showReassurancePage();
  };

  return (
    <div className="question_page">
      {loading && !showReassurance && <Loading />}
      {!loading && !showReassurance && (
        <div className="">
          <Nav sidebar onVersionChange={setActiveVersion} />
          <div className="containerContent questionContent">
            <QuestionCard
              wdim={questions[number].wdim}
              question={questions[number].statement}
              answers={questions[number].answerList}
              callback={nextQuestion}
              showWdimPage={showWdimPage}
              code={questions[number].code}
            />

            <progress max={100} value={progress * 100} />
            <button type="button" onClick={previousQuestion} className="back-button">
              {t('questions.backButton')}
            </button>
          </div>
        </div>
      )}
      {!loading && showReassurance && !showWdim && (
        <Reassurance callback={showReassurancePage} previousQuestion={previousQuestion} progress={progress} />
      )}
      {!loading && !showReassurance && showWdim && <Wdim text={questions[number].wdim} callback={showWdimPage} />}
    </div>
  );
}
